import type {
  PromotionPlanTask,
  PromotionPlanTaskStatus,
} from '../../types/promotionPlan';
import { formatRelativeTime } from '../../lib/relativeTime';

// Pure task-ordering + status presentation helpers. Kept out of the
// component so they're unit-testable in isolation (the drag-reorder math in
// reorderTasks is the part most worth pinning down).

export const TASK_STATUS_OPTIONS: Array<{
  value: PromotionPlanTaskStatus;
  label: string;
}> = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'in_review', label: 'In Review' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_LABEL = new Map(
  TASK_STATUS_OPTIONS.map((o) => [o.value, o.label] as const),
);

export function taskStatusLabel(status: PromotionPlanTaskStatus): string {
  return STATUS_LABEL.get(status) ?? status;
}

export function taskStatusBadge(status: PromotionPlanTaskStatus): string {
  switch (status) {
    case 'not_started':
      return 'bg-slate-100 text-slate-700';
    case 'on_hold':
      return 'bg-amber-100 text-amber-800';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800';
    case 'in_review':
      return 'bg-violet-100 text-violet-800';
    case 'completed':
      return 'bg-emerald-100 text-emerald-800';
  }
}

// Stable comparator within a group: manual `position` ascending, ties broken
// by createdAt so the order never jitters between renders.
function byPositionThenCreated(
  a: PromotionPlanTask,
  b: PromotionPlanTask,
): number {
  if (a.position !== b.position) return a.position - b.position;
  const at = Date.parse(a.createdAt);
  const bt = Date.parse(b.createdAt);
  const av = Number.isNaN(at) ? 0 : at;
  const bv = Number.isNaN(bt) ? 0 : bt;
  return av - bv;
}

// Spec §5.4: completed tasks float to the bottom (and the row renders
// struck-through). Non-completed keep their manual order above; completed
// keep their manual order among themselves. Does not mutate the input.
export function sortTasksForDisplay(
  tasks: PromotionPlanTask[],
): PromotionPlanTask[] {
  const open = tasks.filter((t) => t.status !== 'completed');
  const done = tasks.filter((t) => t.status === 'completed');
  open.sort(byPositionThenCreated);
  done.sort(byPositionThenCreated);
  return [...open, ...done];
}

// Drag-reorder math. Given the display-ordered ids and a move (drag `fromId`
// onto `toId`), return the new id order. The caller then assigns sequential
// positions (index) and persists the ones that changed. Pure + total: a
// no-op move, an unknown id, or fromId === toId all return the input order
// unchanged so the component never has to special-case them.
export function reorderIds(
  orderedIds: string[],
  fromId: string,
  toId: string,
): string[] {
  if (fromId === toId) return orderedIds;
  const from = orderedIds.indexOf(fromId);
  const to = orderedIds.indexOf(toId);
  if (from === -1 || to === -1) return orderedIds;
  const next = orderedIds.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return orderedIds;
  next.splice(to, 0, moved);
  return next;
}

// After a reorder, map each id to its new sequential position and return only
// the tasks whose position actually changed (minimises PATCH calls — a drag
// near the top of a long list shouldn't rewrite every row).
export function changedPositions(
  tasks: PromotionPlanTask[],
  newOrderedIds: string[],
): Array<{ id: string; position: number }> {
  const current = new Map(tasks.map((t) => [t.id, t.position] as const));
  const out: Array<{ id: string; position: number }> = [];
  newOrderedIds.forEach((id, index) => {
    if (current.get(id) !== index) out.push({ id, position: index });
  });
  return out;
}

// Completion-attribution line shown only in the EXPANDED task view (spec
// §5.4). Returns null for any non-completed task (the collapsed list stays
// scannable — no attribution there). The date is relative for recent /
// absolute for older via the shared formatRelativeTime helper. `name` is the
// resolved display name of completedBy, or null when completedBy is null
// (legacy data) or the user can't be resolved — in which case the "by …"
// suffix is dropped.
export function formatTaskCompletion(
  task: Pick<PromotionPlanTask, 'status' | 'completedAt'>,
  name: string | null,
  now: Date = new Date(),
): string | null {
  if (task.status !== 'completed') return null;
  const when = task.completedAt
    ? formatRelativeTime(task.completedAt, now)
    : '';
  const base = when ? `Completed ${when}` : 'Completed';
  return name ? `${base} by ${name}` : base;
}
